<template>
  <div class="TBusinessWidget">
    <div class="TBusinessWidget__grid">
      <!-- ───── Заголовок ───── -->
      <div class="grid gap-3 items-center justify-center text-center">
        <span class="font-semibold text-3xl">Оплата заказа</span>
        <span v-if="orderId" class="text-xl text-gray-500">Заказ №{{ orderId }}</span>
        <span v-if="amount" class="text-base font-bold text-orange-600">
          К оплате: {{ amount }} ₽
        </span>
      </div>

      <!-- ───── Ошибка ───── -->
      <div v-if="paymentError" class="text-center text-red-500 py-8">
        {{ paymentError }}
      </div>

      <!-- ───── Сайт: встроенный виджет T-Business ───── -->
      <template v-if="useEmbeddedWidget">
        <!-- Лоадер загрузки виджета -->
        <div v-if="isLoadingWidget" class="flex flex-col items-center justify-center py-8">
          <svg class="spinner-loader mb-2" width="48" height="48" viewBox="0 0 50 50">
            <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"/>
          </svg>
          <span class="text-base text-gray-700 dark:text-gray-300">Загрузка формы оплаты…</span>
        </div>

        <!-- Контейнер для T-Business-виджета -->
        <div
          v-show="!isLoadingWidget"
          id="paymentContainer"
          class="min-h-[240px] w-full"
          style="color-scheme: light"
        ></div>

        <!-- Ссылка-фоллбэк, если виджет не отрисовался -->
        <div class="text-center text-sm">
          <span>Если у вас нет виджета оплаты, перейдите по ссылке ниже 👇</span>
          <a :href="paymentStore.confirmationUrl" target="_blank" class="p-3">
            {{ paymentStore.confirmationUrl }}
          </a>
        </div>
      </template>

      <!-- ───── Приложение: платёжная форма в системном браузере ───── -->
      <template v-else>
        <p class="TBusinessWidget__hint">
          Оплата откроется в защищённом окне Т-Банка. Способ оплаты — T-Pay, SberPay,
          СБП или карта — выбирается там.
        </p>

        <button
          type="button"
          class="TBusinessWidget__pay"
          @click="openPaymentPage"
        >
          Перейти к оплате
        </button>
      </template>

      <!-- ───── Индикатор ожидания ───── -->
      <div v-if="isProcessing" class="text-center text-orange-500 mt-2">
        Ожидаем подтверждения оплаты...
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { consola } from 'consola'
import apiProfileInstance from '~/entities/user';
import { usePaymentStore, usePaymentReconciliation } from '@features/payment'
import { useInstitutionStore } from '@entities/institution'
import { useModalStore , modalStateEnum } from '@shared/model'
import { isNativePlatform, openExternalUrl, closeCapacitorBrowser } from '@shared/lib'

import { useCartStore } from '@entities/order'

const logger = consola.withTag('TBusinessWidget')

/* ── STORES ── */
const storeInstitution = useInstitutionStore()
const paymentStore     = usePaymentStore()
const modalStore       = useModalStore()
const cartStore        = useCartStore()
const { pollUntilPaid } = usePaymentReconciliation()

/* ── REFS ── */
const orderId         = computed(() => paymentStore.orderId)
const amount          = computed(() => paymentStore.amount ?? null)
const confirmationUrl = computed(() => paymentStore.confirmationUrl)
const isProcessing    = ref(false)
const paymentError    = ref<string | null>(null)
/* ── Новый флаг — лоадер загрузки виджета ── */
const isLoadingWidget = ref(true)

/**
 * Встроенный виджет грузим только на сайте.
 *
 * В приложении так нельзя: Т-Банк требует Chrome Custom Tabs / SFSafariViewController и прямо
 * запрещает встраивание платёжной формы в WebView приложения
 * (developer.tbank.ru/eacq/scenarios/payments/mobile). Технически это ещё и не работает:
 * весь виджет живёт на securepay.tinkoff.ru под сертификатом НУЦ Минцифры, корня которого нет
 * в системных хранилищах доверия Android и iOS, — рукопожатие падает с ERR_CERT_AUTHORITY_INVALID
 * и блок с кнопками T-Pay / SberPay / СБП остаётся пустым.
 */
const useEmbeddedWidget = !isNativePlatform()

/* ── HELPERS ── */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false

  const userAgent = window.navigator.userAgent.toLowerCase()

  // Проверяем основные мобильные платформы
  const mobileKeywords = [
    'android', 'iphone', 'ipad', 'ipod', 'blackberry',
    'windows phone', 'opera mini', 'mobile',
  ]

  const isMobile = mobileKeywords.some(keyword => userAgent.includes(keyword))

  // Дополнительная проверка через touch support
  const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  // Проверяем ширину экрана как дополнительный индикатор
  const isSmallScreen = window.innerWidth <= 768

  const result = isMobile || (hasTouchSupport && isSmallScreen)

  logger.log('📱 Mobile detection:', {
    userAgent: userAgent.slice(0, 50) + '...',
    isMobile,
    hasTouchSupport,
    isSmallScreen,
    screenWidth: window.innerWidth,
    result,
  })

  return result
}

function handleClose() {
  // В приложении платёжная форма открыта поверх нас в системном браузере: пока его не закрыть,
  // клиент не увидит ни «Спасибо за заказ», ни очищенную корзину.
  if (!useEmbeddedWidget) {
    void closeCapacitorBrowser()
  }

  modalStore.close()
  modalStore.open(modalStateEnum.ModalThankYou)
}

/** Платёжная форма Т-Банка (pay.tbank.ru) в системном браузере — путь для приложения. */
async function openPaymentPage(): Promise<void> {
  if (!confirmationUrl.value) return

  logger.log('Открываем платёжную страницу Т-Банка', { orderId: orderId.value })
  await openExternalUrl(confirmationUrl.value)
}

function handleResult(result: 'succeeded' | 'failed') {
  if (result === 'succeeded') {
    handleClose()
  } else {
    paymentStore.setStatus('failed')
    paymentStore.setError('Оплата не удалась')
  }
}

/* ── TERMINAL KEY ── */
const terminalKey = ref(storeInstitution.institution?.tbusiness_terminal_key)

/* ── Поллинг статуса заказа ──
 * Закрывает модалку при оплате независимо от коллбэка SDK — например, когда платят
 * по fallback-ссылке в другой вкладке. Запускаем до загрузки виджета (поллинг важен
 * даже если форма не отрисовалась), останавливаем на размонтировании. */
let stopPoll: (() => void) | null = null

onBeforeUnmount(() => {
  stopPoll?.()
  stopPoll = null
})

/* ── ON MOUNT ── */
onMounted(async () => {
  isProcessing.value = true
  paymentError.value = null

  stopPoll = pollUntilPaid(orderId.value, () => handleResult('succeeded'))

  /* Приложение: сразу уводим в системный браузер, кнопка остаётся как повтор,
   * если окно закрыли. integration.js не грузим вовсе — в WebView он всё равно не работает. */
  if (!useEmbeddedWidget) {
    isLoadingWidget.value = false

    if (!confirmationUrl.value) {
      paymentError.value = 'Не удалось получить ссылку для оплаты. Попробуйте оформить заказ заново.'
      isProcessing.value = false
      logger.error('[TBusiness] Не пришла ссылка на оплату')
      return
    }

    await openPaymentPage()
    return
  }

  try {
    /* 1. Загрузка integration.js */
    if (!(window as any).PaymentIntegration) {
      await new Promise<void>((resolve, reject) => {
        const s = document.createElement('script')
        s.src = 'https://acq-paymentform-integrationjs.t-static.ru/integration.js'
        s.async = true
        s.addEventListener('load', () => resolve())
        s.addEventListener('error', () =>
          reject(new Error('Не удалось загрузить интеграционный скрипт T-Business')),
        )
        document.head.append(s)
      })
    }

    /* 2. Глобальная конфигурация */
    const initConfig = {
      terminalKey: terminalKey.value,
      product    : 'eacq',
      features   : { payment: {} },
    }

    /* 3. Инициализация SDK */
    const integration = await (window as any).PaymentIntegration.init(initConfig)

    /* 4. Создание виджета с коллбэком статуса + глушим редирект */
    const MAIN_INTEGRATION_NAME   = 'main-integration';
    const MAIN_INTEGRATION_CONFIG = {
      router: {
        redirectCallback: async (url: string) => {
          logger.log('🔗 T-Business redirectCallback called with URL:', url)

          if (isMobileDevice()) {
            logger.log('📱 Mobile device detected, opening banking app with direct location change')

            // Для мобильных устройств используем прямое изменение location
            // Это самый надежный способ запуска банковских приложений
            try {
              window.location.href = url
            } catch (error) {
              logger.error('❌ Error with location.href, trying fallback:', error)
              // Fallback: если location.href не сработал
              const opened = window.open(url, '_blank')
              if (!opened) window.open(url, '_self')
            }
          } else {
            logger.log('💻 Desktop device, opening in new tab')
            // На десктопе открываем в новой вкладке
            const popup = window.open(url, '_blank')

            // Если popup заблокирован, информируем пользователя
            if (!popup || popup.closed) {
              logger.warn('⚠️ Popup blocked, user needs to allow popups')
              // Можно добавить уведомление пользователю о необходимости разрешить popup'ы
            }
          }
        },
      },
      status: {
        changedCallback: async (status: string) => {
          if (status === 'SUCCESS') {
            try {
              // Оплата уже прошла — падение очистки не должно ронять обработчик.
              // Корзину досверит reconcilePaidOrder по статусу заказа.
              cartStore.clearCart().catch(() => {});
              paymentStore.setStatus('succeeded');

              // подтягиваем актуальный статус заказа
              const { status: st, payment_status } =
                await apiProfileInstance.getOrder(orderId.value);
              paymentStore.updateOrderField('status', st);
              paymentStore.updateOrderField('payment_status', payment_status);
            } catch (error) {
              logger.warn('[TBusiness] getOrder error:', error);
            }

            // ⬇️ Удаляем виджет-оверлей, если он остался в DOM
            const overlay = document.querySelector('#payment-widgets-overlay-iframe');
            if (overlay) {
              overlay.style.display = 'none';
              setTimeout(() => overlay.remove(), 300);
            }

            handleResult('succeeded');
          } else if (
            ['CANCELED', 'EXPIRED', 'REFUNDED', 'REJECTED', 'PROCESSING_ERROR']
              .includes(status)
          ) {
            handleResult('failed');
          }
        },
      },
    };

    const mainPaymentIntegration =
      await integration.payments.create(MAIN_INTEGRATION_NAME, MAIN_INTEGRATION_CONFIG);

    /* 5. Монтирование */
    const container = document.querySelector('#paymentContainer')
    await mainPaymentIntegration.mount(container)

    /* Когда DOM-виджет отрисовался, прячем лоадер */
    isLoadingWidget.value = false

    /* 6. Callback, возвращающий PaymentURL */
    await integration.payments.setPaymentStartCallback(async () => confirmationUrl.value)

    /* 7. Получаем и применяем payMethods */
    try {
      const resp = await fetch(
        `https://securepay.tinkoff.ru/platform/api/v1/widget/settings/${terminalKey.value}`,
      )
      const payMethods = (await resp.json()).payMethods ?? []

      const widgetMap: Record<string, string> = {
        tinkoffpay: 'tpay',
        tpay      : 'tpay',
        sbp       : 'sbp',
        sberpay   : 'sberpay',
        mirpay    : 'mirpay',
        bnpl      : 'bnpl',
      }

      const mapped = payMethods
        .map((m: string) => widgetMap[m.toLowerCase()])
        .filter(Boolean)

      if (mapped.length) {
        await mainPaymentIntegration.updateWidgetTypes(mapped)
      }
    } catch (error) {
      logger.warn('[TBusiness] Не удалось получить payMethods:', error)
    }

    isProcessing.value = false
  } catch (error: any) {
    paymentError.value = 'Не удалось отобразить платёжную форму.'
    isProcessing.value = false
    isLoadingWidget.value = false
    logger.error('[TBusiness] Ошибка:', error)
  }
})
</script>

<style lang="scss">
.TBusinessWidget {
  @apply p-2.5 bg-white dark:bg-gray-900;

  &__grid {
    @apply grid gap-5 content-center md:gap-10 bg-sky-300 dark:bg-zinc-900 rounded-2xl
    pb-4 px-5 md:py-8 md:pr-20 md:pl-10 max-md:min-h-[calc(100vh-20px)]
    max-md:items-center;
  }

  &__hint {
    @apply text-center text-sm text-gray-700 dark:text-gray-300 justify-self-center max-w-md;
  }

  &__pay {
    @apply inline-flex items-center justify-center w-full max-w-md justify-self-center
    px-6 py-3 rounded-xl bg-yellow-400 hover:bg-yellow-500 text-black font-semibold text-lg
    transition-colors;
  }
}

/* Спиннер */
.spinner-loader {
  animation: spin 1s linear infinite;
  display: inline-block;
}
.spinner-loader .path {
  stroke: #3b82f6;
  stroke-linecap: round;
  opacity: 0.9;
}
@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

#paymentContainer {
  height: 240px !important;
  min-height: 240px;
  width: 100%;
}

#payment-widgets-iframe-main-integration,
#paymentContainer iframe {
  height: 100% !important;
  min-height: 240px !important;
  width: 100% !important;
  display: block;
  border: none;
}
</style>
